import { useCallback, useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

type Billetera = {
  saldo_centavos: number;
  municipio_id: number;
};

type Movimiento = {
  id: number;
  tipo: string;
  monto_centavos: number;
  descripcion: string;
  creado_en: string;
};

const MONTOS       = [100000, 500000, 1000000, 2000000];
const MONTO_LABELS = ['$1.000', '$5.000', '$10.000', '$20.000'];

function fmtFecha(s: string): string {
  return new Date(s.replace(' ', 'T')).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function iconoMovimiento(tipo: string): string {
  const map: Record<string, string> = {
    acreditacion:    '⬆️',
    consumo:         '⬇️',
    reserva:         '🔒',
    liberacion:      '🔓',
    consumo_reserva: '⬇️',
  };
  return map[tipo] ?? '↔️';
}

function DevAprobarUltimoPago({ onAprobado }: { onAprobado: () => void }) {
  const [pagoId, setPagoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<string | null>(null);

  async function aprobar() {
    const id = parseInt(pagoId, 10);
    if (!id) return;
    setLoading(true); setRes(null);
    try {
      const data = await api<any>('/pagos/dev-aprobar', {
        method: 'POST', body: JSON.stringify({ pago_id: id }),
      });
      setRes(`✓ Acreditado $${(data.monto_centavos / 100).toFixed(2)} · Saldo: $${(data.saldo_centavos / 100).toFixed(2)}`);
      onAprobado();
    } catch (e: any) {
      setRes('✗ ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border border-dashed border-amber-300 bg-amber-50 rounded-xl p-3 space-y-2">
      <p className="text-xs text-amber-700 font-semibold">⚠️ Solo dev — simular aprobación de pago</p>
      <div className="flex gap-2">
        <input
          type="number"
          value={pagoId}
          onChange={(e) => setPagoId(e.target.value)}
          placeholder="ID del pago (ver DB)"
          className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs text-gray-900 outline-none"
        />
        <button
          onClick={aprobar}
          disabled={loading || !pagoId}
          className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg px-3 py-1.5 text-xs font-semibold"
        >
          {loading ? '…' : 'Aprobar'}
        </button>
      </div>
      {res && <p className="text-xs text-amber-700">{res}</p>}
    </div>
  );
}

export default function Billetera() {
  const ciudadano = useAuth((s) => s.ciudadano);

  const [billetera, setBilletera]   = useState<Billetera | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [cargando, setCargando]     = useState(true);
  const [montoIdx, setMontoIdx]     = useState(1);
  const [montoCustom, setMontoCustom] = useState('');
  const [recargando, setRecargando] = useState(false);
  const [err, setErr]               = useState<string | null>(null);
  const [msg, setMsg]               = useState<string | null>(null);

  const cargarDatos = useCallback(async function () {
    setCargando(true);
    try {
      await Promise.all([
        api<Billetera>('/billetera').then(setBilletera),
        api<{ movimientos: Movimiento[] }>('/billetera?action=movimientos')
          .then((r) => setMovimientos(r.movimientos)),
      ]);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  async function recargar() {
    let centavos = montoIdx >= 0 ? MONTOS[montoIdx] : parseInt(montoCustom || '0', 10) * 100;
    if (centavos < 100000) return setErr('Monto mínimo: $1.000');
    setRecargando(true); setErr(null); setMsg(null);
    try {
      const res = await api<{ checkout_url: string; pago_id: number }>('/pagos/iniciar', {
        method: 'POST',
        body: JSON.stringify({ monto_centavos: centavos, municipio_id: ciudadano?.municipio_id }),
      });
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: res.checkout_url });
        setMsg('Redirigido a Mercado Pago. Al finalizar el pago, volvé a la app.');
      } else {
        window.open(res.checkout_url, '_blank');
        setMsg('Se abrió Mercado Pago en una nueva pestaña. Volvé aquí después del pago.');
      }
    } catch (e: any) {
      setErr(e.message || 'Error al iniciar recarga');
    } finally {
      setRecargando(false);
    }
  }

  if (cargando) {
    return (
      <>
        <TopBar title="Billetera" back />
        <p className="text-center text-gray-400 mt-20">Cargando…</p>
      </>
    );
  }

  return (
    <>
      <TopBar title="Billetera municipal" back />
      <main className="px-4 py-5 space-y-5 max-w-md mx-auto pb-24">

        {/* Saldo */}
        <div className="bg-brand-600 rounded-2xl p-6 text-center">
          <p className="text-brand-100 text-xs font-semibold uppercase tracking-wide">Saldo disponible</p>
          <p className="text-4xl font-extrabold mt-2 text-white">
            {billetera ? `$${(billetera.saldo_centavos / 100).toLocaleString('es-AR')}` : '—'}
          </p>
        </div>

        {/* Recargar */}
        <div className="bg-white border border-app rounded-2xl p-4 space-y-3 shadow-sm">
          <p className="font-semibold text-sm text-gray-900">Recargar saldo</p>

          <div className="grid grid-cols-2 gap-2">
            {MONTOS.map((_, i) => (
              <button
                key={i}
                onClick={() => { setMontoIdx(i); setMontoCustom(''); }}
                className={`rounded-xl py-2.5 text-sm font-bold transition-colors ${
                  montoIdx === i
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {MONTO_LABELS[i]}
              </button>
            ))}
          </div>

          <button
            onClick={() => { setMontoIdx(-1); setMontoCustom(''); }}
            className={`w-full rounded-xl py-2.5 text-sm font-bold transition-colors ${
              montoIdx === -1
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Otro monto
          </button>

          {montoIdx === -1 && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-sm font-semibold">$</span>
              <input
                type="number"
                value={montoCustom}
                placeholder="Ingresá el monto"
                autoFocus
                onChange={(e) => setMontoCustom(e.target.value)}
                className="flex-1 bg-white border border-gray-300 rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:border-brand-500 outline-none"
              />
            </div>
          )}

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl p-2">{err}</div>
          )}
          {msg && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-xl p-2">{msg}</div>
          )}

          <button
            onClick={recargar}
            disabled={recargando}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-3 font-semibold"
          >
            {recargando ? 'Redirigiendo…' : 'Ir a pagar con Mercado Pago'}
          </button>
          <p className="text-xs text-gray-400 text-center">
            El saldo se acredita automáticamente al confirmar el pago.
          </p>

          {import.meta.env.DEV && (
            <DevAprobarUltimoPago onAprobado={cargarDatos} />
          )}
        </div>

        {/* Movimientos */}
        <div>
          <p className="font-semibold text-sm text-gray-900 mb-2">Últimos movimientos</p>
          {movimientos.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Sin movimientos aún</p>
          ) : (
            <div className="space-y-2">
              {movimientos.map((m) => (
                <div
                  key={m.id}
                  className="bg-white border border-app rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm"
                >
                  <span className="text-xl flex-shrink-0">{iconoMovimiento(m.tipo)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{m.descripcion}</p>
                    <p className="text-xs text-gray-400">{fmtFecha(m.creado_en)}</p>
                  </div>
                  <span className={`text-sm font-bold flex-shrink-0 ${
                    m.monto_centavos > 0 ? 'text-green-600' : 'text-red-500'
                  }`}>
                    {m.monto_centavos > 0 ? '+' : ''}${(m.monto_centavos / 100).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

      </main>
    </>
  );
}
